import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Coordinates } from '../types';
import {
  dayForecastHasPrecip,
  fetchOpenMeteoDailyForecast,
  filterYmdsWithinHorizon,
  formatInstallDayWeatherLine,
  listHorizonYmdsInTaskRange,
  parseYmd,
  resolveProjectSiteCoords,
  type InstallDayWeather,
  type InstallWeatherSiteInput,
} from '../utils/installWeather';

export type UseInstallWeatherForecastArgs = {
  sites: InstallWeatherSiteInput[];
  /** YYYY-MM-DD dates to load (will be filtered to 10-day horizon). */
  dateYmds: string[];
  enabled?: boolean;
};

export type UseInstallWeatherForecastResult = {
  loading: boolean;
  error: string | null;
  getDayWeather: (ymd: string, projectId?: string) => InstallDayWeather | undefined;
  dayHasRain: (ymd: string, projectId?: string) => boolean;
  getTaskRangeRainyDays: (
    start: Date,
    end: Date,
    projectId?: string,
  ) => InstallDayWeather[];
  formatDayLine: (day: InstallDayWeather) => string;
};

type ResolvedSite = {
  projectId: string;
  coords: Coordinates;
};

export function useInstallWeatherForecast({
  sites,
  dateYmds,
  enabled = true,
}: UseInstallWeatherForecastArgs): UseInstallWeatherForecastResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forecastByProject, setForecastByProject] = useState<Map<string, Map<string, InstallDayWeather>>>(
    () => new Map(),
  );

  const sitesKey = useMemo(
    () =>
      sites
        .map(s => `${s.projectId}:${s.coordinates?.lat ?? ''}:${s.coordinates?.lng ?? ''}:${s.location ?? ''}`)
        .sort()
        .join('|'),
    [sites],
  );

  const horizonYmds = useMemo(() => filterYmdsWithinHorizon(dateYmds), [dateYmds]);
  const horizonYmdsKey = useMemo(() => horizonYmds.join(','), [horizonYmds]);

  const sitesRef = useRef(sites);
  sitesRef.current = sites;

  useEffect(() => {
    if (!enabled || sites.length === 0 || horizonYmds.length === 0) {
      setForecastByProject(new Map());
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resolved: ResolvedSite[] = [];
        const siteList = sitesRef.current;
        for (let si = 0; si < siteList.length; si++) {
          const site = siteList[si];
          const coords = await resolveProjectSiteCoords(site);
          if (coords) {
            resolved.push({ projectId: site.projectId, coords });
          }
        }

        const uniqueCoordKeys = new Map<string, Coordinates>();
        for (let ri = 0; ri < resolved.length; ri++) {
          const r = resolved[ri];
          const key = `${r.coords.lat.toFixed(2)},${r.coords.lng.toFixed(2)}`;
          if (!uniqueCoordKeys.has(key)) uniqueCoordKeys.set(key, r.coords);
        }

        const forecastByCoordKey = new Map<string, Map<string, InstallDayWeather>>();
        const coordFetchPromises: Promise<void>[] = [];
        uniqueCoordKeys.forEach((coords, key) => {
          coordFetchPromises.push(
            (async () => {
              const forecast = await fetchOpenMeteoDailyForecast(coords);
              if (!cancelled) forecastByCoordKey.set(key, forecast);
            })(),
          );
        });
        await Promise.all(coordFetchPromises);

        if (cancelled) return;

        const byProject = new Map<string, Map<string, InstallDayWeather>>();
        for (let ri = 0; ri < resolved.length; ri++) {
          const r = resolved[ri];
          const key = `${r.coords.lat.toFixed(2)},${r.coords.lng.toFixed(2)}`;
          const forecast = forecastByCoordKey.get(key);
          if (forecast) byProject.set(r.projectId, forecast);
        }
        setForecastByProject(byProject);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Weather forecast failed');
          setForecastByProject(new Map());
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, sitesKey, horizonYmdsKey]);

  const getForecastMap = useCallback(
    (projectId?: string): Map<string, InstallDayWeather> | undefined => {
      if (projectId) return forecastByProject.get(projectId);
      const first = forecastByProject.values().next().value;
      return first;
    },
    [forecastByProject],
  );

  const getDayWeather = useCallback(
    (ymd: string, projectId?: string): InstallDayWeather | undefined => {
      return getForecastMap(projectId)?.get(ymd);
    },
    [getForecastMap],
  );

  const dayHasRain = useCallback(
    (ymd: string, projectId?: string): boolean => {
      const day = getDayWeather(ymd, projectId);
      return day ? dayForecastHasPrecip(day) : false;
    },
    [getDayWeather],
  );

  const getTaskRangeRainyDays = useCallback(
    (start: Date, end: Date, projectId?: string): InstallDayWeather[] => {
      const map = getForecastMap(projectId);
      if (!map) return [];
      return listHorizonYmdsInTaskRange(start, end)
        .map(ymd => map.get(ymd))
        .filter((d): d is InstallDayWeather => !!d && dayForecastHasPrecip(d));
    },
    [getForecastMap],
  );

  const formatDayLine = useCallback(
    (day: InstallDayWeather) => formatInstallDayWeatherLine(day, parseYmd(day.ymd)),
    [],
  );

  return {
    loading,
    error,
    getDayWeather,
    dayHasRain,
    getTaskRangeRainyDays,
    formatDayLine,
  };
}
